<template>
  <div ref="dataViewRow">
    <div class="content">
      <div class="api-key-row grid" :style="`grid-template-columns: ${columnsGridTemplate(props.columns)};`">
        <div class="name" :class="shouldHideOnBreakpoint(props.columns, 'label')">
          {{ item.label }}
        </div>
        <div class="prefix" :class="shouldHideOnBreakpoint(props.columns, 'prefix')">
          {{ item.prefix }}
        </div>
        <div v-if="item.revoked_at" class="date" :class="shouldHideOnBreakpoint(props.columns, 'revoked_at')">
          {{ formatDateTime(item.revoked_at) }}
        </div>
        <div v-else-if="item.created_at" class="date" :class="shouldHideOnBreakpoint(props.columns, 'created_at')">
          {{ formatDateTime(item.created_at) }}
        </div>
        <ActionButtons
          v-if="!item.revoked_at"
          :actions="actions"
          :is-visible="isRowHovered"
          :class="shouldHideOnBreakpoint(props.columns, 'actions')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { apiService } from '@/services'
import { format } from 'date-fns'
import { inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import ActionButtons from '@/components/common/action-buttons/ActionButtons.vue'
import { columnsGridTemplate, shouldHideOnBreakpoint } from '@/services/layoutUtils'
import { useElementHover } from '@vueuse/core'
import { trashRedIcon } from '@/components/common/icons'
import { initAndRevealMainModal, hideAndUnmountMainModal, ModalType } from '@/components/modals/modals-util'
import { SETTINGS_API_KEYS_ACTIONS_INJECTION_KEY } from '@/constants'
import type { ApiKeyItem } from '@/services/api-keys/types'
import type { ListColumn, CustomModalProps, DataActions } from '@/interfaces'
import { ButtonVariant } from '@/styles/types'

interface Props {
  item: ApiKeyItem
  columns: ListColumn[]
}

const props = defineProps<Props>()

const { t } = useI18n()
const store = useStore()

const dataViewRow = ref<HTMLElement>()
const isRowHovered = useElementHover(dataViewRow)

const dataActions = inject<DataActions>(SETTINGS_API_KEYS_ACTIONS_INJECTION_KEY)

function formatDateTime(date: string): string {
  return format(new Date(date), 'MM/dd/yyyy HH:mm')
}

const actions = [
  {
    label: 'delete',
    handler: handleRevokeApiKey,
    translationKey: 'api-keys-revoke-tooltip',
    isDisabled: false
  }
]

async function revokeApiKey() {
  await store.dispatch('modal/startSubmitting')
    try {
      await apiService.apiKeys.deleteApiKey(props.item.id)
      store.dispatch('messages/addMessage', { message: t('api-keys-revoke-success') })
      dataActions?.refreshData()
    } catch {
      store.dispatch('messages/addMessage', { message: t('api-keys-revoke-error') })
    } finally {
      await store.dispatch('modal/stopSubmitting')
    hideAndUnmountMainModal()
  }
}

function handleRevokeApiKey() {
  const modalProps: CustomModalProps = {
    headerProps: {
      icon: trashRedIcon,
      text: t('api-keys-revoke-title'),
      textSecondary: t('api-keys-revoke-description')
    },
    footerProps: {
      buttonCancel: t('cancel'),
      buttonConfirm: t('api-keys-revoke-tooltip'),
      isConfirmValid: true,
      cancelBtnVariant: ButtonVariant.TERTIARY_GRAY,
    },
    onConfirm: revokeApiKey
  }

  initAndRevealMainModal({
    modalType: ModalType.REVOKE_API_KEY_MODAL,
    props: modalProps
  })
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.content {
  height: fit-content;
}

.grid {
  display: grid;
}

.api-key-row {
  height: 54px;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: $bodyText;
  padding-inline: 1rem;

  &:hover {
    background: $rowSelectedBackground;
  }

  .name, .prefix {
    padding-inline-end: 1rem;
  }
}
</style>
